import { useMemo, useState, type FC, type ReactNode } from "react";
import Button from "../../../components/staff/Button";
import Plus from "../../../components/Atomos/Icons/EditMode/Plus";
import Modal from "../../../components/Atomos/Modals/Modal";
import type { IOfficeNew } from "../../../interfaces/models/IOffice";
import type {
  IContactInfo,
  IOfficeMainContact,
} from "../../../interfaces/models/IOfficeMainContact";
import {
  mergeOfficeMainContactsWithOffices,
  officeMainContactBlockKey,
} from "../../../utils/officeMainContactUtils";
import { formatProfessionalsOfficeFilterLabel } from "./professionalsOfficeFilterLabel";
import { useStaffMode } from "../../../hooks/StaffModeProvider";
import ProfileOfficeTabs, {
  useProfileOfficeTabSelection,
} from "./ProfileOfficeTabs";
import ManageOfficeMainContactsModal from "./ManageOfficeMainContactsModal";
import {
  ResponsiveProfileRecords,
  type ProfileRecordItem,
} from "./ResponsiveProfileRecords";

export type MainContactProfileProps = {
  officeMainContactData: IOfficeMainContact[];
  offices: IOfficeNew[];
  firmId: string;
  onOfficeMainContactChange?: (data: IOfficeMainContact[]) => void;
};

function displayOrEmpty(value: string): ReactNode {
  const trimmed = value.trim();
  if (!trimmed) {
    return <span className="text-gray-300 italic">Not Assigned</span>;
  }
  return trimmed;
}

function buildMainContactRows(contacts: IContactInfo[]): {
  rowKey: string;
  name: string;
  email: string;
  phone: string;
}[] {
  return contacts.map((contact) => ({
    rowKey: contact.contactId,
    name: contact.name?.trim() ?? "",
    email: contact.email?.trim() ?? "",
    phone: contact.phone?.trim() ?? "",
  }));
}

function toMobileMainContactRecords(
  tableRows: ReturnType<typeof buildMainContactRows>,
): ProfileRecordItem[] {
  return tableRows.map((row) => ({
    key: row.rowKey,
    fields: [
      { label: "Name", value: displayOrEmpty(row.name) },
      { label: "Email", value: displayOrEmpty(row.email) },
      { label: "Phone", value: displayOrEmpty(row.phone) },
    ],
  }));
}

const MainContactProfile: FC<MainContactProfileProps> = ({
  officeMainContactData,
  offices,
  firmId,
  onOfficeMainContactChange,
}) => {
  const [showManageModal, setShowManageModal] = useState(false);
  const { isStaffMode } = useStaffMode();

  const displayDocuments = useMemo(
    () =>
      mergeOfficeMainContactsWithOffices(
        officeMainContactData,
        offices,
        firmId,
      ),
    [officeMainContactData, offices, firmId],
  );

  const officeById = useMemo(
    () =>
      new Map(
        offices.map((office) => [String(office.id ?? "").trim(), office]),
      ),
    [offices],
  );

  const officeTabs = useMemo(
    () =>
      displayDocuments.map((doc) => {
        const office = officeById.get(String(doc.officeId ?? "").trim());
        return {
          id: officeMainContactBlockKey(doc),
          label: office
            ? formatProfessionalsOfficeFilterLabel(office)
            : String(doc.officeId ?? "Office"),
        };
      }),
    [displayDocuments, officeById],
  );

  const { activeTabId, setActiveTabId } =
    useProfileOfficeTabSelection(officeTabs);

  const activeDocument = useMemo(
    () =>
      displayDocuments.find(
        (doc) => officeMainContactBlockKey(doc) === activeTabId,
      ),
    [displayDocuments, activeTabId],
  );

  const activeOfficeId = String(activeDocument?.officeId ?? "").trim();

  const activeContacts = activeDocument?.contacts ?? [];

  const tableRows = useMemo(
    () => buildMainContactRows(activeContacts),
    [activeContacts],
  );

  return (
    <div className="flex flex-col overflow-hidden rounded-md border border-gray-100">
      <div className="flex flex-col gap-4 px-4 py-2">
        <Modal
          isModalOpen={showManageModal}
          toggleModal={() => setShowManageModal(false)}
          title="Manage Office Main Contacts"
          className="max-w-[1000px]"
          bodyClassName="min-h-0 overflow-visible"
        >
          <ManageOfficeMainContactsModal
            firmId={firmId}
            offices={offices}
            officeMainContactData={officeMainContactData}
            initialOfficeId={activeOfficeId}
            onClose={() => setShowManageModal(false)}
            onSaved={(updated) => {
              onOfficeMainContactChange?.(updated);
              setShowManageModal(false);
            }}
          />
        </Modal>
        <div className="flex items-center gap-2">
          <h3
            className={`w-full ${isStaffMode ? "text-secondary" : "text-primary"}`}
          >
            Main Contacts
          </h3>
          <div className="flex items-end justify-end">
            <Button
              text="Manage Main Contacts"
              color={isStaffMode ? "secondary" : "primary"}
              variant="filled"
              size="fixed"
              onClick={() => setShowManageModal(true)}
              icon={<Plus />}
              labelNonVisible={["xxs", "xs"]}
            />
          </div>
        </div>
        <ProfileOfficeTabs
          tabs={officeTabs}
          activeTabId={activeTabId}
          onSelect={setActiveTabId}
          className="mb-1 -mx-4"
        />
        {activeContacts.length === 0 ? (
          <div className="text-sm italic text-gray-300">
            No main contacts assigned
          </div>
        ) : (
          <div className="gap-2 text-gray-font">
            <ResponsiveProfileRecords
              records={toMobileMainContactRecords(tableRows)}
            />
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="py-2 w-1/3 text-left font-semibold">Name</th>
                    <th className="py-2 w-1/3 text-left font-semibold">Email</th>
                    <th className="py-2 w-1/3 text-left font-semibold">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((row) => (
                    <tr
                      key={row.rowKey}
                      className="border-b border-gray-50 last:border-0"
                    >
                      <td className="py-1 pr-4 align-top">
                        {displayOrEmpty(row.name)}
                      </td>
                      <td className="py-1 pr-4 align-top break-all">
                        {displayOrEmpty(row.email)}
                      </td>
                      <td className="py-3 align-top">
                        {displayOrEmpty(row.phone)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MainContactProfile;
